/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
import axios from 'axios';
import { addDays, subDays } from 'date-fns';
import moment, { Moment } from 'moment';
import { v4 as uuid } from 'uuid';

import { ReservationById } from '@/types/Reservations';

import { formatedDateOfRequest } from '@/utils/Formatter';
import { phoneToNumber } from '@/utils/InputMask/Number';

import { Mode } from '@/context/ViewMode/type';

import request from '../request';

import {
  BlockFormData,
  PaymentStatus,
  Reservation,
  ReservationConciliate,
  ReservationFormData,
  ReservationPatchProps,
  ReservationReallocateProps,
  ReservationStatus,
} from './types';

export const postReservation = async (values: ReservationFormData) => {
  let guestId = values.guest;

  if (values.isNewGuest) {
    const { data: guest } = await request.post('/account/guest/', {
      user: {
        first_name: values.guest,
        last_name: values.lastName,
        email: values.email,
        phone_number1: phoneToNumber(values.phone1),
        birth_date: formatedDateOfRequest(values.born.toString()),
        gender: values.gender,
        main_role: 'Guest',
        is_individual: values.typePerson === 'individual',
        cpf: values.cpf,
        cnpj: values.cnpj === '' ? null : values.cnpj,
        corporate_name: values.corporateName,
        trading_name: values.tradingName,
        main_address: {
          street: values.street,
          number: values.number,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          postal_code: values.zipCode,
          country: values.country,
        },
      },
    });

    guestId = guest.id;
  }

  await Promise.all(
    (values.properties || []).map((property) =>
      request.post('/calendar/reservations/', {
        code: uuid(),
        guest: guestId,
        check_in_date: formatedDateOfRequest(values.checkInDate.toString()),
        check_out_date: formatedDateOfRequest(values.checkOutDate.toString()),
        total_price: values.totalValue,
        recommended_by: values.indication,
        comment: values.notes,
        paid_amount: values.totalPaid,
        ota: values.ota.id,
        property: property.id,
        adult_guest_quantity: values.adults,
        child_guest_quantity: values.children,
        status: 'Active',
        has_pet: values.hasPet ? values.petsNumber : 0,
        owner_atm: property.owners[0].id,
      }),
    ),
  );
};

export const postReservationV2 = async (values: ReservationFormData) => {
  let guestId = values.guest;

  if (values.isNewGuest) {
    const { data: guest } = await request.post('/account/guest/', {
      user: {
        first_name: values.guest,
        last_name: values.lastName,
        email: values.email,
        phone_number1: phoneToNumber(values.phone1),
        birth_date: formatedDateOfRequest(values.born.toString()),
        gender: values.gender,
        main_role: 'Guest',
        is_individual: values.typePerson === 'individual',
        cpf: values.cpf,
        cnpj: values.cnpj === '' ? null : values.cnpj,
        corporate_name: values.corporateName,
        trading_name: values.tradingName,
        main_address: {
          street: values.street,
          number: values.number,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          postal_code: values.zipCode,
          country: values.country,
        },
      },
    });

    guestId = guest.id;
  }

  const data = await request.post('/calendar/reservations/', {
    code: uuid(),
    guest: guestId,
    check_in_date: formatedDateOfRequest(values.checkInDate.toString()),
    check_out_date: formatedDateOfRequest(values.checkOutDate.toString()),
    total_price: values.totalValue,
    recommended_by: values.indication,
    comment: values.notes,
    paid_amount: values.totalPaid,
    ota: values.ota.id,
    property: values.propertyId,
    adult_guest_quantity: values.adults,
    child_guest_quantity: values.children,
    status: 'Active',
    has_pet: values.hasPet ? values.petsNumber : 0,
    owner_atm: values.owner_atm,
  });
  return data;
};

export const postBlocking = async (values: BlockFormData, useV2?: boolean) => {
  await Promise.all(
    values.properties.map((property) => {
      let payload = {
        code: uuid(),
        check_in_date: formatedDateOfRequest(values.checkInDate.toString()),
        check_out_date: formatedDateOfRequest(values.checkOutDate.toString()),
        blocking_reason: values.blockingReason.value,
        comment: values.notes,
        property: property.id,
        status: 'Active',
      } as any;

      if (values?.guest) {
        payload = {
          ...payload,
          guest: {
            ...values.guest,
          },
        };
      }

      return request.post(`${useV2 ? '/calendar/v2/blocking/' : '/calendar/blocking/'}`, payload);
    }),
  );
};

export const postHostBlocking = async (values: any, useV2?: boolean) => {
  const payload = {
    code: uuid(),
    check_in_date: formatedDateOfRequest(values.checkInDate.toString()),
    check_out_date: formatedDateOfRequest(values.checkOutDate.toString()),
    blocking_reason: values.blockingReason.value,
    comment: values.notes,
    property: values.property,
    status: 'Active',
    ...(values.guest && { guest: { ...values.guest } }),
  };

  const { data } = await request.post(
    `${useV2 ? '/calendar/v2/blocking/' : '/calendar/blocking/'}`,
    payload,
  );

  return data;
};

const createBlockingByPreparationTime = (origin: Reservation, checkin: Date, checkout: Date) => {
  const blocking: Reservation = {
    ...origin,
    id: `${origin.id}-${checkin}-${checkout}`,
    blocking_reason: 'Preparation',
    is_blocking: true,
    check_in_date: checkin.toISOString().split('T')[0],
    check_out_date: checkout.toISOString().split('T')[0],
  };

  return blocking;
};

interface ConfigBase {
  mode: Mode | null;
}

type Config = Partial<ConfigBase>;

export const getReservations = async (
  dates: Moment[],
  properties: string[],
  config?: Config,
): Promise<Reservation[]> => {
  const userID = config?.mode?.user_id || null;

  const { data } = await request.get('/calendar/reservations/', {
    params: {
      check_in_date: dates[dates.length - 1].format('YYYY-MM-DD'),
      check_out_date: dates[0].format('YYYY-MM-DD'),
      listing: properties.join(','),
      user_id: userID,
    },
  });

  const response: Reservation[] = [...data];

  const createBlocking = (item: Reservation, startDate: Date, endDate: Date) => {
    const blocking = createBlockingByPreparationTime(item, startDate, endDate);

    const alreadyExists = response.some(
      (res) => res.check_in_date === blocking.check_in_date && res.status !== 'Canceled',
    );

    if (!alreadyExists) {
      response.push(blocking);
    }
  };

  data.forEach((item: Reservation) => {
    const extraDayPreparation = item?.listing?.property?.extra_day_preparation || 0;

    if (!item.is_blocking && extraDayPreparation !== 0) {
      const checkinBefore = subDays(new Date(item.check_in_date), extraDayPreparation);
      const checkoutBefore = new Date(item.check_in_date);
      const checkinAfter = new Date(item.check_out_date);
      const checkoutAfter = addDays(new Date(item.check_out_date), extraDayPreparation);

      if (
        // extensão mais adiantada ou reserva original
        !item.has_early_extension &&
        (item.is_early_extension || !item.original_reservation)
      ) {
        createBlocking(item, checkinBefore, checkoutBefore);
      }

      if (
        // extensão mais tardia ou reserva original
        !item.has_late_extension &&
        (item.is_late_extension || !item.original_reservation)
      ) {
        createBlocking(item, checkinAfter, checkoutAfter);
      }
    }
  });

  return response;
};

export const getPaymentStatus = (reservationStatus: ReservationStatus): PaymentStatus => {
  const status: Record<ReservationStatus, PaymentStatus> = {
    Active: 'fullyPaid',
    Concluded: 'fullyPaid',
    Canceled: 'canceled',
    'In progress': 'partiallyPaid',
    'Pre-booking': 'unpaid',
  };

  return status[reservationStatus];
};

export const putReservationStatus = async (
  data: Reservation,
  newStatus: ReservationStatus,
  newComment: string,
  id: number,
) => {
  await request.put(`/calendar/reservations/${id}/`, {
    ...data,
    guest: data.guest.id,
    status: newStatus,
    comment: newComment,
  });
};

export const postExtendReservation = async (newExtension: Reservation): Promise<Reservation> => {
  const { data } = await request.post('/calendar/reservations/', {
    ...newExtension,
    guest: newExtension.guest.id,
    ota: newExtension?.listing?.ota?.id,
    property: newExtension?.listing?.property?.id,
    original_reservation: newExtension?.id,
    check_in_date: formatedDateOfRequest(newExtension?.check_in_date),
    check_out_date: formatedDateOfRequest(newExtension?.check_out_date),
    paid_amount: newExtension?.paid_amount,
    total_price: newExtension?.total_price,
    is_early_extension: newExtension?.is_early_extension,
    is_late_extension: newExtension?.is_late_extension,
  });
  return data;
};

export const putFieldsOriginalReservation = async (
  id: number,
  data: Reservation,
  hasEarlyExtension: boolean,
  hasLateExtension: boolean,
  EarlyExtensionReservation: number | null,
  LateExtensionReservation: number | null,
) => {
  await request.put(`/calendar/reservations/${id}/`, {
    ...data,
    guest: data.guest.id,
    has_early_extension: hasEarlyExtension,
    has_late_extension: hasLateExtension,
    early_extension_reservation: EarlyExtensionReservation,
    late_extension_reservation: LateExtensionReservation,
  });
};

export const getReservationById = async (id: number): Promise<Reservation> => {
  const { data } = await request.get(`/calendar/reservations/${id}/`);
  return data;
};

export const patchReservation = async (id: number, data: ReservationPatchProps) =>
  request.patch<Reservation>(`/calendar/reservations/${id}/`, {
    ...data,
  });

export const putReservationReallocate = async (data: ReservationReallocateProps) =>
  request.put<Reservation>('/reservations/relocate/', {
    ...data,
  });

export const putReservationsConciliate = async (data: ReservationConciliate) =>
  request.put('/reservations/conciliate/', {
    ...data,
  });

export const getReservationDataById = async (
  checkIn: any,
  checkOut: any,
  propertyId?: any,
  statushost?: string,
) => {
  const { data } = await request.get(
    `/reservations/calendar?start_date=${checkIn}&end_date=${checkOut}&property_ids=${propertyId}&status=${statushost}`,
  );

  const response: any = [...data];

  data.forEach((item: any) => {
    if (!item.is_blocking && item?.property?.extra_day_preparation !== 0) {
      const checkinBefore = subDays(
        new Date(item.check_in_date),
        item?.property?.extra_day_preparation,
      );
      const checkoutBefore = new Date(item.check_in_date);
      const blockingBefore = createBlockingByPreparationTime(item, checkinBefore, checkoutBefore);
      response.push(blockingBefore);

      const checkinAfter = new Date(item.check_out_date);
      const checkoutAfter = addDays(
        new Date(item.check_out_date),
        item?.property?.extra_day_preparation,
      );
      const blockingAfter = createBlockingByPreparationTime(item, checkinAfter, checkoutAfter);
      response.push(blockingAfter);
    }
  });

  return response;
};

export const getReservationDetailsById = async (reservationId: number) => {
  const { data } = await request.get<ReservationById>(`reservations/calendar/${reservationId}`);

  return data;
};

export const getReservationPaymentsId = async (reservationId: number) => {
  const { data } = await request.get<ReservationById>(
    `reservations/calendar/${reservationId}/payments`,
  );

  return data;
};

type AvailabilityDatesParamsProps = {
  propertyId: number;
  startDate: string;
  endDate: string;
  source: any;
  contextUsed: 'multicalendar' | 'owner_calendar';
  config?: Config;
};

export const getAvailabilityDates = async (payload: AvailabilityDatesParamsProps) => {
  // HARD CODE implementado em 17/10/2024 que futuramente precisa ser revisto e refatorado:

  // Estávamos com um bug no calendário de disponibilidade de datas (componente DatePickerRange com markers).
  // O problema era que bloqueios por tempo de preparo não estavam sendo marcados no calendário como indisponíveis.
  // Devido à urgência desse bug, foi alinhado com o time a implementação de uma solução temporária pelo front, mesmo sabendo
  // que o cenário ideal seria a api `property/availability/ já retornar todas as datas indisponíveis quando estas já estiverem
  // ocupadas seja por reservas ou bloqueios de quaisquer tipos.

  const { propertyId, startDate, endDate, source, contextUsed } = payload;

  // Função auxiliar para adicionar datas entre check-in e check-out ao objeto "availability"
  const addUnavailableDatesBetweenCheckinAndCheckout = (
    availability: any,
    checkinDate: string,
    checkoutDate: string,
  ) => {
    let currentDate = moment(checkinDate).clone();
    const end = moment(checkoutDate).clone().subtract(1, 'day');

    while (currentDate.isSameOrBefore(end)) {
      availability[currentDate.format('YYYY-MM-DD')] = false;
      currentDate = currentDate.add(1, 'day');
    }

    return availability;
  };

  // Função que define a lista de disponibilidade de datas a ser usada no multicalendário
  const getAvailabilityDatesOnMulticalendar = async () => {
    try {
      const reservations = await getReservationDataById(
        moment(startDate).format('YYYY-MM-DD'),
        moment(endDate).format('YYYY-MM-DD'),
        propertyId,
        'Concluded,Active,In progress,Pre-booking,Not confirmed,No-Show,Canceled',
      );

      const availability: any = {};
      [...reservations]
        .filter((reservation: any) => `${reservation?.status}` !== 'Canceled')
        .forEach((item: any) => {
          addUnavailableDatesBetweenCheckinAndCheckout(
            availability,
            item?.check_in_date,
            item?.check_out_date,
          );
        });

      const { data } = await request.get(`availability/property`, {
        params: {
          property_id: propertyId,
          start_date: startDate,
          end_date: endDate,
        },
        cancelToken: source.token,
      });

      const response: any = {
        results: {
          ...data.results,
          ...availability,
        },
      };

      return response;
    } catch (err) {
      if (axios.isCancel(err)) {
        return 'Request Canceled!';
      }
      return err;
    }
  };

  // Função que define a lista de disponibilidade de datas a ser usada no calendário do proprietário
  const getAvailabilityDatesOnOwnerCalendar = async () => {
    try {
      const dates = [moment(startDate), moment(startDate).add(2, 'months')];
      const reservations: Reservation[] = await getReservations(
        dates,
        [`${propertyId}`],
        payload?.config,
      );

      const availability: any = {};
      [...reservations]
        .filter((reservation: any) => `${reservation?.status}` !== 'Canceled')
        .forEach((item: any) => {
          addUnavailableDatesBetweenCheckinAndCheckout(
            availability,
            item?.check_in_date,
            item?.check_out_date,
          );
        });

      const { data } = await request.get(`availability/property`, {
        params: {
          property_id: propertyId,
          start_date: startDate,
          end_date: endDate,
        },
        cancelToken: source.token,
      });

      const response: any = {
        results: {
          ...data.results,
          ...availability,
        },
      };

      return response;
    } catch (err) {
      if (axios.isCancel(err)) {
        return 'Request Canceled!';
      }
      return err;
    }
  };

  let availabilityDates: any = {};

  switch (contextUsed) {
    case 'multicalendar':
      availabilityDates = await getAvailabilityDatesOnMulticalendar();
      break;
    case 'owner_calendar':
      availabilityDates = await getAvailabilityDatesOnOwnerCalendar();
      break;
    default:
      availabilityDates = {};
      break;
  }

  return availabilityDates;
};
